/**
 * Lokalno skladište vozačevog telefona.
 *
 * Tura se učita jednom, pre polaska, i od tada živi u localStorage. Sve što
 * vozač uradi ide u red za slanje, isto lokalno. Mreža se ne čeka ni za jedan
 * potez — ako je ima, red se prazni u pozadini; ako je nema, red raste i vozač
 * vidi koliko toga još nije otišlo.
 *
 * Zapisi se brišu tek kad server potvrdi. Server prima ponovljeno slanje bez
 * posledica, pa je bolje poslati dvaput nego izgubiti jednom.
 */

const TOUR_KEY = 'elbraco.vozac.tura';

/**
 * Cija je tura u kesu.
 *
 * Bez ovoga je ekran pokazivao poslednju ucitanu turu bilo kom vozacu koji
 * otvori link, jer se tura cita iz localStorage pre nego sto se bilo sta pita
 * server. Na sluzbenom telefonu koji menja ruke to je tudja tura na ekranu.
 */
const TOUR_DRIVER_KEY = 'elbraco.vozac.id';
const OUTBOX_KEY = 'elbraco.vozac.red';
const OPEN_STOP_KEY = 'elbraco.vozac.otvorena';
const REJECTED_KEY = 'elbraco.vozac.odbijeno';

/** Zapis koji server nije primio, sa razlogom koji je dao. */
export interface OdbijenZapis {
  event: DriverEvent;
  razlog: string;
}
const NAV_SEEN_KEY = 'elbraco.vozac.navigacija';

export interface DriverStopItem {
  id: number;
  articleCode: string;
  articleName: string;
  quantity: number;
  isDelivered: boolean;
  deliveredAt: string | null;
  /**
   * Roba za kupca na stanici objekta (ključ otpremnice) — kupac dolazi po nju.
   * Predaje se odvojeno od dopune. Stari server polje ne šalje.
   */
  zaKupca?: string | null;
}

export interface DriverStop {
  id: number;
  sequence: number;
  plannedSequence: number;
  kind: 'NetworkLocation' | 'CustomerAddress';
  /**
   * Roba se ovde UZIMA, ne ostavlja - prodavnica koja je aparat prodala.
   * Sve ostalo na ekranu je isporuka, pa se ovo mora videti odmah.
   */
  isPickup: boolean;
  /**
   * Stanica dodata pošto je vozač već krenuo, sa razlogom. Prazno kod stanica
   * iz prvobitne ture.
   */
  addedAt?: string | null;
  addedReason?: string | null;
  /**
   * Stanica skinuta sa ture. Ne briše se sa spiska nego se precrtava: tačka
   * koja nestane izgleda kao kvar, pa bi je vozač isporučio za svaki slučaj.
   */
  removedAt?: string | null;
  removedReason?: string | null;
  name: string;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  floor: number | null;
  hasLift: boolean | null;
  accessNotes: string | null;
  requiresTwoPeople: boolean;
  /**
   * Na otpremnici pise "bb" - kuca nema broj, pa je tacka na ulici a ne na
   * kapiji. Vozac to mora videti pre nego sto krene da trazi brojeve.
   */
  withoutHouseNumber: boolean;
  /**
   * Kupcevi telefoni. Za adresu upisanu kao "bb" ovo je jedino resenje.
   * Ume ih biti vise - ERP u istom polju cuva i broj drugog ukucanina.
   */
  phones: string[];
  /**
   * Kupac NIJE dobio SMS da roba stize danas - u ERP-u mu stoji samo fiksni
   * broj, ili broj nije citljiv. Izmereno: svaki sedmi.
   *
   * Stoji uz telefone jer se resava telefonom. Vozac ionako zove pred dolazak;
   * ovoga treba pozvati ranije, da ne stane pred vrata coveka koji ga ne
   * ocekuje.
   */
  customerNotAnnounced: boolean;
  /**
   * Stanica zatvorena odgovorom pri kraju ture, ili operaterovom potvrdom.
   * Vremena tada ostaju prazna — pitanje je stiglo posle, ne na stanici.
   */
  deliveryConfirmedAt?: string | null;
  actualArrival: string | null;
  actualDeparture: string | null;
  exceptionReason: string | null;
  notes: string | null;
  hasSignature: boolean;
  items: DriverStopItem[];
}

export interface DriverTour {
  id: number;
  name: string;
  date: string;
  status: string;
  /**
   * Verzija ture. Raste kad operater doda ili skine stanicu pošto je vozač
   * već dobio turu. Telefon je poredi sa serverom i po tome zna da je zaostao.
   *
   * Starije kopije u telefonu je nemaju — tada se uzima 1, jer neizmenjena
   * tura i jeste prva verzija.
   */
  version?: number;

  /**
   * Koliko se strogo trazi potpis: `trazi`, `obavezan` ili `ne_trazi`.
   *
   * Stize UZ TURU da bi vazilo i bez mreze. Starije kopije u telefonu ga nemaju
   * — tada se ponasa kao `trazi`, sto je i podrazumevano pravilo.
   */
  signatureRule?: string;

  /** Kad je javljeno da je vozilo stalo. Prazno: nije javljeno. */
  breakdownReportedAt?: string | null;
  vehicle: string | null;
  driver: string | null;
  actualStart: string | null;
  actualEnd: string | null;
  /**
   * Linija stvarnog puta, [lat, lon]. Stiže UZ TURU da bi skica postojala i
   * bez signala. Prazno kad rutiranje nije odgovorilo — tada se crtaju prave
   * linije između stanica.
   */
  roadGeometry?: [number, number][];
  start: { name: string; latitude: number | null; longitude: number | null };
  end: { name: string; latitude: number | null; longitude: number | null };
  stops: DriverStop[];
}

export interface DriverEvent {
  clientEventId: string;
  type: string;
  occurredAt: string;
  stopId?: number;
  stopItemId?: number;
  text?: string;
  sequence?: number;
  signature?: string;
  /** Kupčeva ocena, 1–5 zvezdica (samo uz `EventType.CustomerRated`). */
  rating?: number;
  /**
   * Tura i ključ vozača kome zapis PRIPADA — upisuju se pri nastanku.
   *
   * Analiza 2.0, 14.10.2026: na službenom telefonu koji menja ruke red se
   * slao na turu koja je TRENUTNO u telefonu, ključem iz linka. Neposlato
   * „tura završena" vozača A tako je zatvaralo turu vozača B. Ostaju samo na
   * telefonu; server ih ne čita. Stari zapisi ih nemaju — oni idu kao i ranije.
   */
  tourId?: number;
  accessKey?: string;
}

/** Zapis bez lokalnih oznaka — onako kako ga server prima. */
export function zaServer(event: DriverEvent): DriverEvent {
  const ostalo: DriverEvent = { ...event };
  delete ostalo.tourId;
  delete ostalo.accessKey;
  return ostalo;
}

/**
 * Red podeljen po turi i ključu kome zapisi pripadaju. Zapis bez oznake
 * (nastao pre nego što su oznake postojale) ide uz turu i ključ iz telefona,
 * kao što je i do tada išao.
 */
export function podeliPoTuri(
  red: DriverEvent[], tekucaTura: number | null, tekuciKljuc: string
): { tourId: number; accessKey: string; events: DriverEvent[] }[] {
  const grupe = new Map<string, { tourId: number; accessKey: string; events: DriverEvent[] }>();

  for (const e of red) {
    const tourId = e.tourId ?? tekucaTura;
    if (tourId === null || tourId === undefined) continue;

    const accessKey = e.accessKey ?? tekuciKljuc;
    const kljuc = `${tourId}|${accessKey}`;

    let grupa = grupe.get(kljuc);
    if (!grupa) {
      grupa = { tourId, accessKey, events: [] };
      grupe.set(kljuc, grupa);
    }
    grupa.events.push(e);
  }

  return Array.from(grupe.values());
}

/**
 * Šalje jednu grupu reda; kad šef u međuvremenu napravi NOV link, šalje je
 * novim ključem istog vozača.
 *
 * Analiza scenarija (K5), 16.10.2026: zapis nosi ključ iz trenutka nastanka.
 * Kad šef napravi nov link, server stari ključ odbija (401) — i sve što je vozač
 * uradio bez signala ostajalo je zauvek u telefonu. Isti paket se sad šalje
 * ključem iz linka koji je vozač otvorio; server i tada proverava da je tura
 * njegova. Izgubljen telefon i dalje ne može ništa da pošalje: nalazač nema nov
 * link.
 *
 * `tudja`: ni novi ključ ne vidi turu (404) — to je zapis drugog vozača na
 * deljenom telefonu. Ostaje njemu i ne prijavljuje se kao greška.
 */
export async function posaljiGrupu(
  fetchFn: typeof fetch,
  base: string,
  grupa: { tourId: number; accessKey: string },
  telo: string,
  tekuciKljuc: string
): Promise<{ response: Response; prekljuceno: boolean; tudja: boolean }> {
  const salji = (kljuc: string) => fetchFn(
    `${base}/api/driver/tours/${grupa.tourId}/sync?kljuc=${encodeURIComponent(kljuc)}`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: telo });

  const prvi = await salji(grupa.accessKey);

  if (prvi.status !== 401 || !tekuciKljuc || grupa.accessKey === tekuciKljuc) {
    return { response: prvi, prekljuceno: false, tudja: false };
  }

  const drugi = await salji(tekuciKljuc);
  return { response: drugi, prekljuceno: drugi.ok, tudja: drugi.status === 404 };
}

export const EventType = {
  TourStarted: 'tura.krenula',
  ArrivedAtStop: 'stanica.stigao',
  DepartedFromStop: 'stanica.otisao',
  ItemDelivered: 'stavka.isporucena',
  StopException: 'stanica.izuzetak',
  StopReordered: 'stanica.redosled',
  SignatureCaptured: 'stanica.potpis',
  NoteAdded: 'stanica.beleska',
  TourFinished: 'tura.zavrsena',

  /**
   * Odgovor pri zatvaranju ture, za stanicu koja nije zatvorena u toku dana.
   *
   * Ne upisuje vreme odlaska: odgovor stiže na kraju ture, a ne na stanici.
   * Upisati tadašnje vreme značilo bi izmisliti trajanje isporuke.
   */
  StopClosedAtEnd: 'stanica.zatvorena-na-kraju',

  /** Pogrešan štrik vraćen — stavka ipak nije predata. Sme dok tura nije zatvorena. */
  ItemUndelivered: 'stavka.vracena',

  /**
   * Kupac je ocenio prevoz i isporuku (1–5 zvezdica), u kućici za potpis.
   * Ide u ISTOM paketu sa potpisom — server broji samo ocenu uz potpis.
   */
  CustomerRated: 'stanica.ocena',

  /**
   * Vozilo je stalo — kvar, gume, nezgoda.
   *
   * Ne zatvara turu: kvar nije ishod nego stanje, i moze da se popravi za pola
   * sata. Sta dalje odlucuje operater.
   */
  VehicleBrokeDown: 'tura.kvar'
} as const;

/**
 * Lokalno vreme uređaja, bez vremenske zone.
 *
 * Namerno se ne šalje UTC ni offset: beleži se sat koji je vozač gledao kad je
 * predao robu. Vreme sinhronizacije server upisuje sam, i razlika između ta dva
 * je podatak o tome koliko se radilo bez signala.
 */
export function deviceNow(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
    + `T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
  );
}

export function newId(): string {
  // crypto.randomUUID ne postoji na starijim Android pregledačima.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // Puno skladište je stvaran rizik zbog potpisa. Ne ruši se ekran, ali se
    // greška ne guta — vozač mora videti da nešto nije sačuvano.
    console.error('Lokalno skladište ne prima više zapisa.', e);
    throw e;
  }
}

export const storage = {
  /**
   * Koja je stanica bila otvorena.
   *
   * Vozač sa ovog ekrana odlazi u Mape, i telefon aplikaciju tada ume da ugasi
   * u pozadini. Kad se vrati, nastavlja tamo gde je stao — a ne traži svoju
   * stanicu među četrnaest.
   */
  openStop: (): number | null => {
    const value = read<number | null>(OPEN_STOP_KEY, null);
    return typeof value === 'number' ? value : null;
  },

  rememberOpenStop: (stopId: number | null) => write(OPEN_STOP_KEY, stopId),

  /** Da li je vozaču već objašnjeno kako se vraća sa Mapa. */
  navigationExplained: (): boolean => read<boolean>(NAV_SEEN_KEY, false) === true,

  markNavigationExplained: () => write(NAV_SEEN_KEY, true),

  /**
   * Tura iz kesa, bez obzira cija je. Koristi je sinhronizacija: red mora da
   * se isprazni i kad je na ekranu neko drugi.
   */
  loadTour: (): DriverTour | null => read<DriverTour | null>(TOUR_KEY, null),

  /**
   * Cijim kljucem je tura ucitana, ili nista ako kes jos nije stampan.
   *
   * Pamti se da bi se tudja tura iz kesa prepoznala: telefon zna da predje iz
   * ruke u ruku, a vozac ne sme da vidi tudje kupce.
   */
  tourDriverKey: (): string | null => read<string | null>(TOUR_DRIVER_KEY, null),

  saveTour: (tour: DriverTour, accessKey: string) => {
    // Zapisi iz verzije pre oznaka (`DriverEvent.tourId`) pripadaju turi koja
    // je DO SADA bila u telefonu. Pre nego što je nova tura pregazi, dobijaju
    // njenu oznaku — inače bi posle promene vozača otišli na tuđu turu.
    const ranija = read<DriverTour | null>(TOUR_KEY, null);
    const raniji = read<string | null>(TOUR_DRIVER_KEY, null);
    const red = read<DriverEvent[]>(OUTBOX_KEY, []);

    if (ranija && raniji && red.some((e) => e.tourId === undefined)) {
      write(OUTBOX_KEY, red.map((e) => (e.tourId === undefined
        ? { ...e, tourId: ranija.id, accessKey: e.accessKey ?? raniji }
        : e)));
    }

    write(TOUR_KEY, tour);
    write(TOUR_DRIVER_KEY, accessKey);
  },

  clearTour: () => {
    localStorage.removeItem(TOUR_KEY);
    localStorage.removeItem(TOUR_DRIVER_KEY);
  },

  outbox: (): DriverEvent[] => read<DriverEvent[]>(OUTBOX_KEY, []),

  /**
   * Neposlato koje pripada OVOJ turi. Zapis bez oznake se računa uz turu u
   * telefonu, kao i ranije.
   */
  outboxFor: (tourId: number): DriverEvent[] => {
    const uTelefonu = read<DriverTour | null>(TOUR_KEY, null)?.id ?? null;
    return read<DriverEvent[]>(OUTBOX_KEY, [])
      .filter((e) => (e.tourId ?? uTelefonu) === tourId);
  },

  /**
   * Upisuje događaj u red. Vraća ga, da pozivalac odmah primeni i lokalno.
   * Zapis nosi turu i ključ iz telefona u trenutku nastanka — vidi `tourId`.
   */
  enqueue: (event: Omit<DriverEvent, 'clientEventId' | 'occurredAt'>): DriverEvent => {
    const tura = read<DriverTour | null>(TOUR_KEY, null);
    const kljuc = read<string | null>(TOUR_DRIVER_KEY, null);
    const full: DriverEvent = {
      ...event,
      clientEventId: newId(),
      occurredAt: deviceNow(),
      ...(tura ? { tourId: tura.id } : {}),
      ...(kljuc ? { accessKey: kljuc } : {})
    };
    write(OUTBOX_KEY, [...read<DriverEvent[]>(OUTBOX_KEY, []), full]);
    return full;
  },

  /**
   * Zapisi koje je server ODBIO, sa razlogom.
   *
   * Analiza 2.0, 14.10.2026: odbijen zapis je ostajao u redu zauvek — red
   * nikad nije bio prazan, pa izmene ture nisu stizale do telefona, a „uzmi
   * sledeću turu" se nije pojavljivalo. Ponovno slanje ga ne bi primilo (server
   * odbija po pravilu, ne zbog mreže), pa se seli ovamo: vozač ga vidi i javlja
   * šefu, a red se prazni.
   */
  odbijeno: (): OdbijenZapis[] => read<OdbijenZapis[]>(REJECTED_KEY, []),

  /** Seli odbijene zapise iz reda u fioku „server nije primio". */
  odbij: (odbijeni: { clientEventId: string; reason?: string | null }[]) => {
    if (odbijeni.length === 0) return;

    const razlozi = new Map(odbijeni.map((o) => [o.clientEventId, o.reason ?? 'bez razloga']));
    const red = read<DriverEvent[]>(OUTBOX_KEY, []);

    write(REJECTED_KEY, [
      ...read<OdbijenZapis[]>(REJECTED_KEY, []),
      ...red.filter((e) => razlozi.has(e.clientEventId))
        .map((e) => ({ event: e, razlog: razlozi.get(e.clientEventId)! }))
    ]);
    write(OUTBOX_KEY, red.filter((e) => !razlozi.has(e.clientEventId)));
  },

  /**
   * Fotografija koju server nije primio ide u istu fioku — vozač ima jedno
   * mesto na kom vidi šta treba da javi šefu.
   */
  odbijFotografije: (odbijene: { id: string; stopId: number; takenAt: string; razlog: string }[]) => {
    if (odbijene.length === 0) return;

    write(REJECTED_KEY, [
      ...read<OdbijenZapis[]>(REJECTED_KEY, []),
      ...odbijene.map((o) => ({
        event: { clientEventId: o.id, type: 'stanica.fotografija', occurredAt: o.takenAt, stopId: o.stopId },
        razlog: o.razlog
      }))
    ]);
  },

  /** Vozač je pročitao i javio — fioka se prazni. */
  ocistiOdbijeno: () => write(REJECTED_KEY, []),

  /** Briše samo ono što je server potvrdio. Odbijeno se seli u `odbij`. */
  forget: (ids: string[]) => {
    const keep = new Set(ids);
    write(OUTBOX_KEY, read<DriverEvent[]>(OUTBOX_KEY, []).filter((e) => !keep.has(e.clientEventId)));
  }
};
